import React from 'react';
import { Bar as BarChart } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import 'chartjs-plugin-datalabels';
import { XsMobile, SmMobile, Desktop } from '../../app-responsive';
import { getDataDefaults, getMobileOptions, getDesktopOptions } from './bar-graph-stacked-config';
import { roundNumberToDecimals } from '../../app-actions';
import TextWithTooltip from '../text-with-tooltip/text-with-tooltip';
import { ROUTE_PATHS } from '../../app-constants';
import { formatUrl } from '../portal-nav/business-solution-nav.presenter';

const GraphWithTooltip = (props) => {
  const { data, options, height, toolTipYLabel, hideYAxisTooltip } = props;
  const tickStyle = {
    color: options.scales.yAxes[0].ticks.fontColor,
  };
  return (
    <React.Fragment>
      {!hideYAxisTooltip &&
        <TextWithTooltip
          text={<i className="td-icon icon-help" />}
          textClassName="help-icon-container ylabel"
          toolTipText={toolTipYLabel}
          id="tooltiptext-bar-graph-stacked-legend-y"
        />
      }
      <div className="graph-max-label" style={tickStyle}>
        MAX
      </div>
      <BarChart data={data} options={options} width={650} height={height} />
      <div className="graph-legend">
        {data.datasets.map((dataSet, index) => {
          const { label, backgroundColor } = dataSet;
          const legendStyle = {
            backgroundColor,
          };
          const isYourProjectionLabel = index === 2;
          const projectionDataIsZero = dataSet.data[0] === 0;
          // remove "Your Projection" from the legend if its data is zero
          if (isYourProjectionLabel && projectionDataIsZero) {
            return null;
          }
          return (
            <div className="graph-legend-item" key={label}>
              <span style={legendStyle} />{label}
            </div>
          );
        })
        }
      </div>
    </React.Fragment>);
};

const BarGraphStacked = (props) => {
  const { data: graphData, totalBenefits, authorableContent, showMarketComparisonLink, hideYAxisTooltip } = props;
  const { heading, description, toolTip, toolTipYLabel } = authorableContent;
  const dataDefaults = getDataDefaults(authorableContent);
  const mobileOptions = getMobileOptions(authorableContent);
  const desktopOptions = getDesktopOptions(authorableContent);

  const prepareDataSet = () => {
    // data defaults and values
    const dataset = [];
    const currentStatus = graphData[0] || 0;
    const totalStatus = roundNumberToDecimals(currentStatus + totalBenefits);
    /* restrict the graph to 100% if value goes higher */
    const totalBenefitsForDisplay = totalStatus > 100 ? 100 - currentStatus : totalBenefits;
    // each data set with params and data
    dataDefaults.datasets.forEach((dataElement, i) => {
      switch (i) {
        case 0: // dataset1 - current status
          dataset.push({
            ...dataElement,
            data: [graphData[i], 0],
          });
          break;
        case 1: // dataset2 - retail market
          dataset.push({ ...dataElement, data: [0, graphData[i]] });
          break;
        default: // dataset3 - total benefits
          dataset.push({
            ...dataElement,
            data: [totalBenefitsForDisplay, 0],
            datalabels: {
              ...dataElement.datalabels,
              // display label only for index 0
              formatter: (value, context) => (context.dataIndex === 0 ? `${totalStatus}%` : ''),
            },
          });
          break;
      }
    });
    return dataset;
  };

  // all datasets for graph render
  const data = { datasets: prepareDataSet(), labels: ['', ''] };
  const heightConfig = {
    xsmobile: 600,
    smmobile: 495,
    desktop: 280,
  };
  const { marketComparison } = ROUTE_PATHS;
  return (
    <div className="bar-graph-stacked bar-graph card row mx-sm-0">
      <div className="card-body">
        <h2 id="graph-heading">
          {heading}
        </h2>
        <TextWithTooltip
          text={<i className="td-icon icon-help" />}
          textClassName="help-icon-container"
          toolTipText={toolTip}
          id="tooltiptext-bar-graph-stacked-heading"
        />
        <p id="graph-description">{description}</p>
        {
          showMarketComparisonLink && (
            <Link to={formatUrl(marketComparison.route)}>
              <button className="button clear left-aligned btn-no-padding">Find out more</button>
            </Link>
          )
        }
        <div className="graph-container">
          <XsMobile>
            <GraphWithTooltip
              data={data}
              options={mobileOptions}
              height={heightConfig.xsmobile}
              toolTipYLabel={toolTipYLabel}
              hideYAxisTooltip={hideYAxisTooltip}
            />
          </XsMobile>
          <SmMobile>
            <GraphWithTooltip
              data={data}
              options={mobileOptions}
              height={heightConfig.smmobile}
              toolTipYLabel={toolTipYLabel}
              hideYAxisTooltip={hideYAxisTooltip}
            />
          </SmMobile>
          <Desktop>
            <GraphWithTooltip
              data={data}
              options={desktopOptions}
              height={heightConfig.desktop}
              toolTipYLabel={toolTipYLabel}
              hideYAxisTooltip={hideYAxisTooltip}
            />
          </Desktop>
        </div>
      </div>
    </div>
  );
};

export default BarGraphStacked;
